"use client"
// Completed & Enhanced hooks for notification management
import { useState, useEffect, useCallback } from "react";
import { useApi } from "@/hooks/useApi";

/**
 * Custom hook for managing user notification preferences
 * Enhanced with proper error handling, loading states, and optimistic updates
 */
export const useNotificationPreferences = () => {
  const { request, isLoading, error } = useApi();
  const [preferences, setPreferences] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  /**
   * Get all notification categories
   */
  const getNotificationCategories = useCallback(async () => {
    try {
      const response = await request('/api/notifications/categories/');
      setCategories(response);
      return response;
    } catch (error) {
      console.error("Error fetching notification categories:", error);
      return [];
    }
  }, [request]);

  /**
   * Get user notification preferences
   */
  const getUserPreferences = useCallback(async () => {
    try {
      const response = await request('/api/notifications/preferences/');
      setPreferences(response);
      return response;
    } catch (error) {
      console.error("Error fetching notification preferences:", error);
      return [];
    }
  }, [request]);

  /**
   * Update a single notification preference with optimistic updates
   */
  const updateNotificationPreference = useCallback(async (id, updatedPreference) => {
    // Optimistic update for UI responsiveness
    const originalPrefs = [...preferences];

    try {
      setIsUpdating(true);
      setUpdateError(null);

      // Update local state immediately
      setPreferences(prev =>
        prev.map(pref => pref.id === id ? { ...pref, ...updatedPreference } : pref)
      );

      // Make API request
      const response = await request(`/api/notifications/preferences/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(updatedPreference)
      });

      return response;
    } catch (error) {
      console.error("Error updating notification preference:", error);
      // Revert back to original state on error
      setPreferences(originalPrefs);
      setUpdateError(error.message || "Failed to update preference");
      throw error;
    } finally {
      setIsUpdating(false);
    }
  }, [preferences, request]);

  /**
   * Get or create default preferences for all categories
   */
  const getDefaultPreferences = useCallback(async () => {
    try {
      const response = await request('/api/notifications/preferences/defaults/');
      setPreferences(response);
      return response;
    } catch (error) {
      console.error("Error fetching default preferences:", error);
    }
  }, [request]);

  /**
   * Update multiple notification preferences at once
   */
  const bulkUpdatePreferences = useCallback(async (updates) => {
    const originalPrefs = [...preferences];

    try {
      setIsUpdating(true);
      setUpdateError(null);

      // Apply updates optimistically to local state
      const updatedPreferences = [...preferences];
      updates.forEach(update => {
        const index = updatedPreferences.findIndex(p => p.id === update.id);
        if (index !== -1) {
          updatedPreferences[index] = { ...updatedPreferences[index], ...update };
        }
      });

      setPreferences(updatedPreferences);

      // Make API request
      const response = await request('/api/notifications/preferences/bulk-update/', {
        method: 'POST',
        body: JSON.stringify(updates)
      });

      return response;
    } catch (error) {
      console.error("Error bulk updating preferences:", error);
      // Revert to original state on error
      setPreferences(originalPrefs);
      setUpdateError(error.message || "Failed to update preferences");
      throw error;
    } finally {
      setIsUpdating(false);
    }
  }, [preferences, request]);

  /**
   * Initialize user preferences by loading categories and preferences
   */
  const initializePreferences = useCallback(async () => {
    try {
      const categories = await getNotificationCategories();
      const prefs = await getUserPreferences();

      // If no preferences exist, get defaults
      if (!prefs || prefs.length === 0) {
        await getDefaultPreferences();
      }

      return { categories, preferences: prefs };
    } catch (error) {
      console.error("Error initializing preferences:", error);
    }
  }, [getNotificationCategories, getUserPreferences, getDefaultPreferences]);

  /**
   * Enable or disable all preferences
   */
  const updateAllPreferences = useCallback(async (enabled) => {
    try {
      setIsUpdating(true);

      // Create updates for all preferences
      const updates = preferences.map(pref => ({
        id: pref.id,
        enabled
      }));

      // Update all preferences locally first
      setPreferences(prev =>
        prev.map(pref => ({ ...pref, enabled }))
      );

      // Call the API to update
      await bulkUpdatePreferences(updates);
    } catch (error) {
      console.error("Error updating all preferences:", error);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  }, [preferences, bulkUpdatePreferences]);

  return {
    preferences,
    categories,
    isLoading,
    isUpdating,
    error: error || updateError,
    getNotificationCategories,
    getUserPreferences,
    updateNotificationPreference,
    getDefaultPreferences,
    bulkUpdatePreferences,
    initializePreferences,
    updateAllPreferences
  };
};

/**
 * Custom hook for managing user notifications
 */
export const useUserNotifications = () => {
  const { request, isLoading, error } = useApi();
  const [data, setData] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  /**
   * Get user notifications with optional filtering
   */
  const getUserNotifications = useCallback(async (
    acknowledged,
    category,
    limit
  ) => {
    try {
      // Build query params
      const params = new URLSearchParams();
      if (acknowledged !== undefined) {
        params.append('acknowledged', String(acknowledged));
      }
      if (category !== undefined) {
        params.append('category', String(category));
      }
      if (limit !== undefined) {
        params.append('limit', String(limit));
      }

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await request(`/api/notifications${queryString}`);

      setData(response);
      return response;
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [request]);


  /**
   * Mark a notification as acknowledged
   * @param id Notification ID
   */
  const acknowledgeNotification = async (id: string): Promise<void> => {
    try {
      await request(`/api/notifications/acknowledge/`, {
        method: "PUT",
        body: JSON.stringify({ notificationId: id }),
      });
    } catch (error) {
      console.error("Error acknowledging notification:", error);
      throw error;
    }
  };

  /**
   * Mark all notifications as acknowledged
   * @param category Optional category ID to only acknowledge notifications from that category
   */
  const acknowledgeAllNotifications = async (category?: number): Promise<void> => {
    try {
      let url = `/api/notifications/acknowledge-all/`;
      if (category) {
        url += `?category=${category}`;
      }

      await request(url, {
        method: "PUT",
      });
    } catch (error) {
      console.error("Error acknowledging all notifications:", error);
      throw error;
    }
  };

  /**
   * Get notification count
   */
  const getNotificationCount = useCallback(async (groupByCategory = false) => {
    try {
      const url = `/api/notifications/unread-count/?group_by=${groupByCategory}`;
      const response = await request(url);

      // Set the unread count if it's a simple count
      if (!groupByCategory && response && response.count !== undefined) {
        setUnreadCount(response.count);
      }

      return response;
    } catch (error) {
      console.error("Error fetching notification count:", error);
    }
  }, [request]);

  /**
   * Get notifications grouped by category for digest view
   */
  const getNotificationDigest = useCallback(async (
    limitPerCategory = 5,
    showAcknowledged = false
  ) => {
    try {
      const url = `/api/notifications/digest/?limit_per_category=${limitPerCategory}&acknowledged=${showAcknowledged}`;
      return await request(url);
    } catch (error) {
      console.error("Error fetching notification digest:", error);
    }
  }, [request]);

  /**
   * Initialize notifications data by fetching counts and latest notifications
   */
  const initializeNotificationsData = useCallback(async () => {
    try {
      const count = await getNotificationCount();
      const notifications = await getUserNotifications(false, undefined, 10);

      return { count, notifications };
    } catch (error) {
      console.error("Error initializing notifications data:", error);
    }
  }, [getNotificationCount, getUserNotifications]);

  /**
   * Get a single notification by ID
   * @param id Notification ID
   */
  const getNotificationById = async (id: string): Promise<Notification | undefined> => {
    try {
      const response = await request(`/api/notifications/${id}/`);
      return response?.data
    } catch (error) {
      console.error(`Error fetching notification ${id}:`, error);
    }
  };

  return {
    data,
    unreadCount,
    isLoading,
    error,
    getUserNotifications,
    acknowledgeNotification,
    acknowledgeAllNotifications,
    getNotificationCount,
    getNotificationDigest,
    initializeNotificationsData,
    getNotificationById
  };
};